import axios, {
  AxiosProgressEvent,
  AxiosRequestConfig,
  AxiosResponse,
} from "axios";

// Api Manager Types
import { IResponse } from "./ApiManagerTypes";

// Api Manager Types
import { EAppConstants } from "../../components/shared/commonTypes/commonTypes";

// Axios instance initialization
const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_PATH,
  headers: {
    "Content-Type": "application/json",
  },
  validateStatus: (status) => {
    // Accept status codes between 200 and 400 as successful responses
    return status >= 200 && status <= 400;
  },
});

// Auth API endpoint to attach auth api token
const authUrls = ["/login", "/auth"];

// Interceptor to handle JWT token and error handling
api.interceptors.request.use(
  (config) => {
    let token: string | null;
    if (!authUrls.some((url) => config.url?.includes(url))) {
      token = localStorage.getItem(EAppConstants.APP_SESSION_TOKEN_KEY ?? "");
    } else {
      token = localStorage.getItem(EAppConstants.APP_AUTH_TOKEN_KEY ?? "");
    }
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// Interceptor to handle response error
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      window.location.href = "/authenticate";
      localStorage.clear();
    }
    if (error.response?.status === 403) {
      window.location.href = "/accessdenied";
    }
    return Promise.reject(error);
  },
);

// Api request handler
async function apiRequest<T extends IResponse>(
  method: "get" | "post" | "delete",
  endpoint: string,
  params: any,
  data?: unknown,
  config: AxiosRequestConfig = {},
): Promise<AxiosResponse<T>> {
  const url = endpoint;
  return await api.request<T>({ method, url, params, data, ...config });
}

// Get api request
export async function getApi<T extends IResponse>(
  endpoint: string,
  params?: any,
  config?: AxiosRequestConfig,
): Promise<IResponse> {
  return (await apiRequest<T>("get", endpoint, params, undefined, config)).data;
}

// Get export api request
// Returns whether the export/download completed successfully so callers can
// surface an in-progress/success/error status (e.g. CommonExportPopper's
// download-status notification) without relying on a thrown rejection —
// existing callers that don't inspect the return value are unaffected.
export async function getExportApi(
  endpoint: string,
  params?: any,
  config?: AxiosRequestConfig,
): Promise<boolean> {
  try {
    const response = await api.get(endpoint, {
      params,
      responseType: "blob",
      ...config,
    });

    // Extract filename from header
    const contentDisposition = response.headers["content-disposition"];
    let fileName = "";
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^"]+)"?/);
      if (match && match[1]) {
        fileName = match[1];
      }
    }

    // Create a Blob and trigger download
    const blob = new Blob([response.data], {
      type: response.headers["content-type"],
    });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
    return true;
  } catch (error) {
    console.error("Export API error:", error);
    return false;
  }
}

// Post export api request
export async function postExportApi(
  endpoint: string,
  data?: any,
  config?: AxiosRequestConfig,
): Promise<void> {
  try {
    const response = await api.post(endpoint, data, {
      responseType: "blob",
      ...config,
    });

    // Extract filename from header
    const contentDisposition = response.headers["content-disposition"];
    let fileName = "";
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^"]+)"?/);
      if (match && match[1]) {
        fileName = match[1];
      }
    }

    // Create a Blob and trigger download
    const blob = new Blob([response.data], {
      type: response.headers["content-type"],
    });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  } catch (error) {
    console.error("Export API error:", error);
  }
}

// Post api request
export async function postApi<T extends IResponse>(
  endpoint: string,
  data?: unknown,
  config?: AxiosRequestConfig,
): Promise<IResponse> {
  return (await apiRequest<T>("post", endpoint, undefined, data, config)).data;
}

// Post file api
export async function postFileApi<T extends IResponse>(
  endpoint: string,
  data?: FormData,
  config?: AxiosRequestConfig,
): Promise<IResponse> {
  const response = await api.post<IResponse>(endpoint, data, {
    headers: { "Content-Type": "multipart/form-data" },
    ...config,
  });
  return response.data;
}

// Delete api request
export async function deleteApi<T extends IResponse>(
  endpoint: string,
  data?: unknown,
  config?: AxiosRequestConfig,
): Promise<IResponse> {
  return (await apiRequest<T>("delete", endpoint, undefined, data, config))
    .data;
}

// Put file api. Bare axios on purpose: the presigned S3 URL must not receive
// the bearer token. `onUploadProgress` is optional, so existing callers are
// unaffected.
export async function putFileApi(
  endpoint: string,
  data: Blob,
  onUploadProgress?: (event: AxiosProgressEvent) => void,
  contentType = "application/octet-stream",
): Promise<unknown> {
  const response = await axios.put<unknown>(endpoint, data, {
    headers: { "Content-Type": contentType },
    onUploadProgress,
  });
  return response.data;
}
